import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSuccessionTheme } from '../SuccessionThemeProvider';
import { withSnackbar } from 'notistack';
import { useIntl } from 'react-intl';
import GuidedTour from '../../Shared/GuidedTour/GuidedTour';
import ProfileHeaderSection from './sections/ProfileHeaderSection';
import ProfileBioSection from './sections/ProfileBioSection';
import TransitionGoalsCard from './sections/TransitionGoalsCard';
import ProfessionalDetailsSection from './sections/ProfessionalDetailsSection';
import PersonalInsightsSection from './sections/PersonalInsightsSection';
import PracticeInformationSection from './sections/PracticeInformationSection';
import RevenueInformationSection from './sections/RevenueInformationSection';
import ProfessionalCredentialsSection from './sections/ProfessionalCredentialsSection';
import ProfessionalPlanningSection from './sections/ProfessionalPlanningSection';
import AdditionalPracticeInformationSection from './sections/AdditionalPracticeInformationSection';
import ProfileHeaderEditModal from './modals/ProfileHeaderEditModal';
import ProfileBioEditModal from './modals/ProfileBioEditModal';
import TransitionGoalsEditModal from './modals/TransitionGoalsEditModal';
import PracticeInformationEditModal from './modals/PracticeInformationEditModal';
import RevenueInformationEditModal from './modals/RevenueInformationEditModal';
import MultiSelectEditModal from './modals/MultiSelectEditModal';
import AvatarSelectionModal from './modals/AvatarSelectionModal';
import AskMeAboutEditModal from './modals/AskMeAboutEditModal';
import ContinuityPlanningEditModal from './modals/ContinuityPlanningEditModal';
import AdditionalPracticeInformationEditModal from './modals/AdditionalPracticeInformationEditModal';
import SuccessionProfileDrawerNew from '../ProfileDrawer/SuccessionProfileDrawerNew';
import ProfileProgressBanner from './ProfileProgressBanner';
import { AvatarUpdateBanner } from '../../AvatarDisplay';
import { getAvatarStyle } from '../../../lib/s3';

const PROFILE_TOUR_STORAGE_KEY_PREFIX = 'profile_tour_shown_v1_';

const SuccessionProfileEditPage = ({ user, options, refetchUser, enqueueSnackbar, companySettings = {}, quickbooksConnected, quickbooksLastSyncedAt, quickbooksConnectUrl, quickbooksDisconnectUrl, quickbooksSyncUrl }) => {
  const intl = useIntl();
  const { theme } = useSuccessionTheme();
  const isReadOnly = !!(user?.company?.imported_production_data_read_only);
  const companyName = user?.company?.name || 'your firm';
  const guidedTourEnabled = !!user?.company?.feature_types?.includes('guided_tour');
  const userId = user?.id;

  // Modal state management
  const [activeModal, setActiveModal] = useState(null);
  const [showPublicPreview, setShowPublicPreview] = useState(false);
  const [drawerKey, setDrawerKey] = useState(0);
  const [tourOpen, setTourOpen] = useState(false);

  // Auto-open tour on first visit (per-user localStorage gate); supports ?tour=open / ?tour=reset
  useEffect(() => {
    if (!userId || !guidedTourEnabled) return;
    const storageKey = `${PROFILE_TOUR_STORAGE_KEY_PREFIX}${userId}`;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('tour') === 'reset') {
        localStorage.removeItem(storageKey);
        setTourOpen(true);
        return;
      }
      if (params.get('tour') === 'open') {
        setTourOpen(true);
        return;
      }
      if (!localStorage.getItem(storageKey)) {
        const timer = setTimeout(() => setTourOpen(true), 500);
        return () => clearTimeout(timer);
      }
    } catch (_) {}
  }, [userId, guidedTourEnabled]);

  const handleTourClose = () => {
    setTourOpen(false);
    if (userId) {
      try { localStorage.setItem(`${PROFILE_TOUR_STORAGE_KEY_PREFIX}${userId}`, '1'); } catch (_) {}
    }
  };

  const tourSteps = useMemo(() => ([
    {
      id: 'profile-sections',
      title: intl.formatMessage({ id: 'succession.profile_edit.tour.sections_title', defaultMessage: 'Your profile sections' }),
      description: intl.formatMessage({
        id: 'succession.profile_edit.tour.sections_description',
        defaultMessage: 'Your profile is organized into sections — header, bio, practice, revenue, credentials, and more. Scroll through to review each one.',
      }),
      targetSelector: '[data-tutorial-id="profile-sections"]',
      position: 'right',
    },
    {
      id: 'profile-edit',
      title: intl.formatMessage({ id: 'succession.profile_edit.tour.edit_title', defaultMessage: 'Edit any section' }),
      description: intl.formatMessage({
        id: 'succession.profile_edit.tour.edit_description',
        defaultMessage: 'Use the Edit button on any section to open a focused editor and update just that part of your profile. Changes save instantly.',
      }),
      targetSelector: '[data-tutorial-id="profile-edit-button"]',
      position: 'left',
    },
    {
      id: 'profile-public-view',
      title: intl.formatMessage({ id: 'succession.profile_edit.tour.public_view_title', defaultMessage: 'Preview your public view' }),
      description: intl.formatMessage({
        id: 'succession.profile_edit.tour.public_view_description',
        defaultMessage: 'See exactly how other advisors and matches view your profile in the marketplace.',
      }),
      targetSelector: '[data-tutorial-id="profile-public-view"]',
      position: 'bottom',
    },
  ]), [intl]);
  
  const handleCloseModal = () => {
    setActiveModal(null);
  };
  
  const handleSaveSuccess = (message) => {
    enqueueSnackbar(message || intl.formatMessage({ id: 'succession.profile_edit.page.profile_updated' }), { variant: 'success' });
    refetchUser();
    handleCloseModal();
  };

  const handleSaveError = (error) => {
    enqueueSnackbar(error.message || intl.formatMessage({ id: 'succession.profile_edit.page.failed_to_update' }), { variant: 'error' });
  };

  const handleAvatarSaveSuccess = (message, avatarFilename = null) => {
    handleSaveSuccess(message);
    if (!avatarFilename) return;

    window.dispatchEvent(new CustomEvent('findbob:avatar-updated', {
      detail: { filename: avatarFilename }
    }));

    // Update server-rendered header avatar(s) without full page reload.
    const updatedAvatarUrl = getAvatarStyle(avatarFilename, companySettings).url;
    document.querySelectorAll('[data-current-user-avatar="true"]').forEach((img) => {
      img.setAttribute('src', updatedAvatarUrl);
    });
  };

  return (
    <div className={`min-h-screen bg-succession-bg-primary ${theme}`}>
      {/* Header with "See Public View" button */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession.profile_edit.page.my_profile' })}</h1>
          <button
            onClick={() => {
              setDrawerKey(prev => prev + 1);
              setShowPublicPreview(true);
            }}
            data-tutorial-id="profile-public-view"
            className="inline-flex items-center px-4 py-2 border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-secondary transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            {intl.formatMessage({ id: 'succession.profile_edit.page.see_public_view' })}
          </button>
        </div>

        {/* Profile Progress Banner */}
        <ProfileProgressBanner user={user} />

        {/* Profile Sections */}
        <div className="space-y-6" data-tutorial-id="profile-sections">
          <AvatarUpdateBanner
            user={user}
            companySettings={companySettings}
            onUpdateClick={() => setActiveModal('avatar')}
          />

          {/*
            `data-profile-section` is how ProfileProgressBanner locates a section
            to scroll to. It replaced index-based lookup, which broke whenever the
            conditionally-rendered AvatarUpdateBanner above was visible. Values are
            the keys in Profile::SectionCompletion::DISPLAY_ORDER — keep the two in
            sync when adding a section.
          */}
          {/* Header Section */}
          <div data-profile-section="header">
            <ProfileHeaderSection
              user={user}
              company={user.company}
              onEdit={() => setActiveModal('header')}
              onAvatarEdit={() => setActiveModal('avatar')}
              companySettings={companySettings}
            />
          </div>

          {/* Transition Goals Card */}
          <div data-profile-section="goals">
            <TransitionGoalsCard
              user={user}
              onEdit={() => setActiveModal('goals')}
            />
          </div>

          {/* Bio Section */}
          <div data-profile-section="bio">
            <ProfileBioSection
              user={user}
              onEdit={() => setActiveModal('bio')}
            />
          </div>

          {/* Revenue Information */}
          <div data-profile-section="revenue">
            <RevenueInformationSection
              user={user}
              onEdit={() => setActiveModal('revenue')}
              isReadOnly={isReadOnly}
              companyName={companyName}
              refetchUser={refetchUser}
              quickbooksConnected={quickbooksConnected}
              quickbooksLastSyncedAt={quickbooksLastSyncedAt}
              quickbooksConnectUrl={quickbooksConnectUrl}
              quickbooksDisconnectUrl={quickbooksDisconnectUrl}
              quickbooksSyncUrl={quickbooksSyncUrl}
            />
          </div>

          {/* Practice Information */}
          <div data-profile-section="practice">
            <PracticeInformationSection
              user={user}
              onEdit={() => setActiveModal('practice')}
            />
          </div>

          {/* Professional Details */}
          <div data-profile-section="professional">
            <ProfessionalDetailsSection
              user={user}
              onEditPrimaryMarkets={() => setActiveModal('primary-markets')}
              onEditSpecializedBusinesses={() => setActiveModal('specialized-businesses')}
              onEditPracticeAreas={() => setActiveModal('practice-areas')}
            />
          </div>

          {/* Personal Insights */}
          <div data-profile-section="personal">
            <PersonalInsightsSection
              user={user}
              onEditWhyValues={() => setActiveModal('why-values')}
              onEditAskMeAbout={() => setActiveModal('ask-me-about')}
              onEditLanguages={() => setActiveModal('languages')}
              onEditInterests={() => setActiveModal('interests-hobbies')}
            />
          </div>

          {/* Professional Credentials */}
          <div data-profile-section="credentials">
            <ProfessionalCredentialsSection
              user={user}
              onEditDesignations={() => setActiveModal('designations')}
              onEditLicenses={() => setActiveModal('licenses')}
            />
          </div>

          {/* Professional Planning — no progress dot; not one of the eight
              display sections */}
          <ProfessionalPlanningSection
            user={user}
            onEditContinuityPlanning={() => setActiveModal('continuity-planning')}
          />

          {/* Additional Practice Information */}
          <AdditionalPracticeInformationSection
            user={user}
            onEdit={() => setActiveModal('additional-practice-information')}
          />

        </div>
      </div>

      {/* Modals */}
      {activeModal === 'avatar' && (
        <AvatarSelectionModal
          user={user}
          avatars={options.avatars}
          onClose={handleCloseModal}
          onSave={handleAvatarSaveSuccess}
          onError={handleSaveError}
          companySettings={companySettings}
        />
      )}

      {activeModal === 'header' && (
        <ProfileHeaderEditModal
          user={user}
          onClose={handleCloseModal}
          onSave={handleSaveSuccess}
          onError={handleSaveError}
        />
      )}

      {activeModal === 'bio' && (
        <ProfileBioEditModal
          user={user}
          onClose={handleCloseModal}
          onSave={handleSaveSuccess}
          onError={handleSaveError}
        />
      )}

      {activeModal === 'goals' && (
        <TransitionGoalsEditModal
          user={user}
          options={options}
          onClose={handleCloseModal}
          onSave={handleSaveSuccess}
          onError={handleSaveError}
        />
      )}

      {activeModal === 'revenue' && (
        <RevenueInformationEditModal
          user={user}
          onClose={handleCloseModal}
          onSave={handleSaveSuccess}
          onError={handleSaveError}
          isReadOnly={isReadOnly}
          companyName={companyName}
          quickbooksConnected={quickbooksConnected}
        />
      )}

      {activeModal === 'practice' && (
        <PracticeInformationEditModal
          user={user}
          onClose={handleCloseModal}
          onSave={handleSaveSuccess}
          onError={handleSaveError}
          isReadOnly={isReadOnly}
          companyName={companyName}
        />
      )}

      {activeModal === 'primary-markets' && (
        <MultiSelectEditModal
          title={intl.formatMessage({ id: 'succession.profile_edit.page.multi_select_title_primary_markets' })}
          selected={user.primary_markets || []}
          options={options.primaryMarkets}
          maxSelections={5}
          onClose={handleCloseModal}
          onSave={(selected) => handleSaveSuccess(intl.formatMessage({ id: 'succession.profile_edit.page.primary_markets_updated' }))}
          field="primary_markets"
          user={user}
          onError={handleSaveError}
          allowCustom={true}
        />
      )}

      {activeModal === 'specialized-businesses' && (
        <MultiSelectEditModal
          title={intl.formatMessage({ id: 'succession.profile_edit.page.multi_select_title_specialized_businesses' })}
          selected={user.specialized_businesses || []}
          options={options.specializedBusinesses}
          maxSelections={5}
          onClose={handleCloseModal}
          onSave={(selected) => handleSaveSuccess(intl.formatMessage({ id: 'succession.profile_edit.page.specialized_businesses_updated' }))}
          field="specialized_businesses"
          user={user}
          onError={handleSaveError}
          allowCustom={true}
        />
      )}

      {activeModal === 'practice-areas' && (
        <MultiSelectEditModal
          title={intl.formatMessage({ id: 'succession.profile_edit.page.multi_select_title_practice_areas' })}
          selected={user.practice_areas || []}
          options={options.practiceAreas}
          maxSelections={5}
          onClose={handleCloseModal}
          onSave={(selected) => handleSaveSuccess(intl.formatMessage({ id: 'succession.profile_edit.page.practice_areas_updated' }))}
          field="practice_areas"
          user={user}
          onError={handleSaveError}
          allowCustom={true}
        />
      )}

      {activeModal === 'why-values' && (
        <MultiSelectEditModal
          title={intl.formatMessage({ id: 'succession.profile_edit.page.multi_select_title_why_values' })}
          selected={user.why_values || []}
          options={options.whyValues}
          maxSelections={5}
          onClose={handleCloseModal}
          onSave={(selected) => handleSaveSuccess(intl.formatMessage({ id: 'succession.profile_edit.page.why_values_updated' }))}
          field="why_values"
          user={user}
          onError={handleSaveError}
          allowCustom={true}
        />
      )}

      {activeModal === 'ask-me-about' && (
        <AskMeAboutEditModal
          user={user}
          onClose={handleCloseModal}
          onSave={handleSaveSuccess}
          onError={handleSaveError}
        />
      )}

      {activeModal === 'languages' && (
        <MultiSelectEditModal
          title={intl.formatMessage({ id: 'succession.profile_edit.page.multi_select_title_languages' })}
          selected={user.languages || []}
          options={options.languages.map(l => l.name)}
          maxSelections={10}
          onClose={handleCloseModal}
          onSave={(selected) => handleSaveSuccess(intl.formatMessage({ id: 'succession.profile_edit.page.languages_updated' }))}
          field="languages"
          user={user}
          onError={handleSaveError}
          isLanguages={true}
          languageOptions={options.languages}
        />
      )}

      {activeModal === 'interests-hobbies' && (
        <MultiSelectEditModal
          title={intl.formatMessage({ id: 'succession.profile_edit.page.multi_select_title_interests_hobbies' })}
          selected={[...(user.interests || []), ...(user.sports || [])]}
          options={[...options.interests.map(i => i.name), ...options.sports.map(s => s.name)]}
          maxSelections={10}
          onClose={handleCloseModal}
          onSave={(selected) => handleSaveSuccess(intl.formatMessage({ id: 'succession.profile_edit.page.interests_updated' }))}
          field="interests_sports"
          user={user}
          onError={handleSaveError}
          isInterestsAndSports={true}
          interestOptions={options.interests}
          sportOptions={options.sports}
        />
      )}

      {activeModal === 'designations' && (
        <MultiSelectEditModal
          title={intl.formatMessage({ id: 'succession.profile_edit.page.multi_select_title_designations' })}
          selected={user.designations || []}
          options={options.designations.map(d => d.name) || []}
          maxSelections={20}
          onClose={handleCloseModal}
          onSave={(selected) => handleSaveSuccess(intl.formatMessage({ id: 'succession.profile_edit.page.designations_updated' }))}
          field="designations"
          user={user}
          onError={handleSaveError}
          allowCustom={true}
        />
      )}

      {activeModal === 'licenses' && (
        <MultiSelectEditModal
          title={intl.formatMessage({ id: 'succession.profile_edit.page.multi_select_title_advisor_licenses' })}
          selected={user.advisor_licenses || []}
          options={options.advisorLicenses || []}
          maxSelections={20}
          onClose={handleCloseModal}
          onSave={(selected) => handleSaveSuccess(intl.formatMessage({ id: 'succession.profile_edit.page.licenses_updated' }))}
          field="advisor_licenses"
          user={user}
          onError={handleSaveError}
          allowCustom={true}
        />
      )}

      {activeModal === 'continuity-planning' && (
        <ContinuityPlanningEditModal
          user={user}
          onClose={handleCloseModal}
          onSave={handleSaveSuccess}
          onError={handleSaveError}
        />
      )}

      {activeModal === 'additional-practice-information' && (
        <AdditionalPracticeInformationEditModal
          user={user}
          onClose={handleCloseModal}
          onSave={handleSaveSuccess}
          onError={handleSaveError}
        />
      )}


      {/* Public Preview Drawer */}
      {showPublicPreview && (
        <SuccessionProfileDrawerNew
          key={`profile-drawer-${drawerKey}`}
          profile={{
            data: {
              getUser: user,
              getCurrentUser: user, // Same as getUser since viewing own profile
            },
          }}
          closeSuccessionDrawer={() => setShowPublicPreview(false)}
          enqueueSnackbar={enqueueSnackbar}
          onEditProfile={() => setShowPublicPreview(false)}
          hideMessagesForOwnProfile={true}
          hideNotebookForOwnProfile={true}
        />
      )}

      {/* Guided Tour — gated by guided_tour feature */}
      {guidedTourEnabled && (
        <GuidedTour
          isOpen={tourOpen}
          steps={tourSteps}
          onClose={handleTourClose}
          onComplete={handleTourClose}
          onReplayRequest={() => setTourOpen(true)}
        />
      )}
    </div>
  );
};

SuccessionProfileEditPage.propTypes = {
  user: PropTypes.object.isRequired,
  options: PropTypes.object.isRequired,
  refetchUser: PropTypes.func.isRequired,
  enqueueSnackbar: PropTypes.func.isRequired,
  companySettings: PropTypes.shape({
    new_avatars_enabled: PropTypes.bool,
    avatar_directory: PropTypes.string,
    avatar_count: PropTypes.number,
    avatar_background_color: PropTypes.string,
    primary_color: PropTypes.string,
  }),
};

export default withSnackbar(SuccessionProfileEditPage);

